import { Component, ElementRef, Inject, OnInit, ViewChild, OnDestroy } from '@angular/core';
import {EsriExpand, EsriReactiveUtils} from '../../../config/esri.module';
import { WidgetNotifyService } from '../../../shared/service/widget-notify.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { SearchListItem, TypeaheadService } from '../../../shared/service/typeahead.service';
import { Observable, Subject } from 'rxjs';
import { QueryService } from '../../../shared/service/query.service';
import { MessageService } from '../../../core/service/message.service';
import { CONSTANTS, CONSTANTS_VALUE } from '../../../config/constants.module';
import { mergeMap, takeUntil } from 'rxjs/operators';
import { HierarchicalSearchConfig, SearchInfo } from 'src/app/config/model/search-config.model';
import { MainMapService } from '../../../shared/service/main-map.service';
import { LayerContainerService } from '../../../shared/service/layer-container.service';
import { ConfigService } from '../../../config/config.service';
import { BaseWidget } from '../base-widget/base-widget';
import { SelectionService } from '../../../shared/service/selection.service';

@Component({
    selector: 'search-hektometer',
    templateUrl: './search-hektometer.component.html',
    standalone: false
})
export class SearchHektometerComponent extends BaseWidget implements OnInit, OnDestroy {
  @ViewChild('searchHektometerComponentRef', { static: true })
  searchHektometerComponentRef: ElementRef;
  @ViewChild('nummerierungRef', { static: true }) nummerierungRef: ElementRef;
  @ViewChild('kmRef', { static: true }) kmRef: ElementRef;

  public formGroup: UntypedFormGroup;
  public searchTextNummerierung: string;
  public searchTextKm: string;
  public searchListNummerierung: Observable<any>;
  public searchListKm: Observable<any>;
  public searchConfig: HierarchicalSearchConfig;
  public currentSelection: __esri.Graphic;

  private destroy$ = new Subject<void>();

  constructor(
    public typeaheadService: TypeaheadService,
    public mainMapService: MainMapService,
    public widgetNotifyService: WidgetNotifyService,
    private _messageService: MessageService,
    private _layerContainerService: LayerContainerService,
    private _queryService: QueryService,
    private selectionService: SelectionService,
    @Inject(CONSTANTS) private CONST: any,
    private _configService: ConfigService
  ) {
    super(mainMapService, widgetNotifyService);
  }

  ngOnInit() {
    // get MainMapService
    const mapView: any = this.mainMapService.mapView;

    // Create an Expand instance and set the content
    const expandProperties: __esri.ExpandProperties = {
      view: mapView,
      id: 'SearchHektometerComponent',
      content: this.searchHektometerComponentRef.nativeElement,
      expandIcon: 'search',
    };

    this.expandWidget = new EsriExpand(expandProperties);
    // add widget to the main map
    mapView.ui.add(this.expandWidget, 'top-right');

    this.initWidgetListeners();
    // create controls
    this.formGroup = new UntypedFormGroup({
      taNummerierung: new UntypedFormControl(),
      taKm: new UntypedFormControl(),
    });

    this.initTypeAheadSearch();
  }

  public initWidgetListeners() {
    this.mainMapService.registerWidget(this.expandWidget);

    // expand widget on create
    if (this.expandOnInit) {
      this.expandWidget.expand();
    }

    // Open this widget, will close other widgets
    EsriReactiveUtils.watch(
      () => this.expandWidget.expanded,
      () => {
        if (this.expandWidget.expanded) {
          this.mainMapService.closeOtherWidgets(this.expandWidget);
          if (this.currentSelection) this.selectAndZoom(this.currentSelection);
        } else {
          if (this.currentSelection) this.selectionService.removeSelectedGraphic(this.currentSelection);
        }
      }
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* Search methods
   ========================== */

  /**
   * @name initTypeAheadSearch
   * @description defines the search source and the search query
   */
  private initTypeAheadSearch() {
    const hSearchInfo: HierarchicalSearchConfig = this.getSearchConfig();

    if (!hSearchInfo.headSearch) throw new Error('there must be a searchField defined in the config');

    const kiloLayer = this._layerContainerService.layerList.filter(
      f => f.esriLayer.id === hSearchInfo.headSearch.fkLayer
    )[0];

    // define search list
    this.searchListNummerierung = Observable.create((observer: any) => {
      observer.next(this.searchTextNummerierung);
    }).pipe(
      mergeMap((token: string) => {
        return this.typeaheadService.querySearchSource(
          kiloLayer.esriLayer['url'],
          hSearchInfo.headSearch.fields[0],
          hSearchInfo.keyField,
          token,
          {
            distinct: true,
            noLimit: true,
            outFields: hSearchInfo.headSearch.fields,
            customWhere: true,
            where: hSearchInfo.headSearch.fields
              .map(t => {
                return 'LOWER(' + t + ") like '%" + token.toLocaleLowerCase() + "%'";
              })
              .join(' OR '),
            nameFormatter: this.formateKilometrierung,
            sort: this.sortAutocomplete,
          }
        );
      })
    );

    const kmLayer = this._layerContainerService.layerList.filter(
      f => f.esriLayer.id === hSearchInfo.baseSearch.fkLayer
    )[0];
    // define search list KM
    this.searchListKm = Observable.create((observer: any) => {
      observer.next(this.searchTextKm);
    }).pipe(
      mergeMap((token: string) => {
        const val: string = this.nummerierungRef.nativeElement.value;
        const _condition = `${hSearchInfo.baseSearch.fields[1]} = ${val ? (val as string).match(/[^-]*/g)[0] : ''}`;
        return this.typeaheadService.querySearchSource(
          kmLayer.esriLayer['url'],
          hSearchInfo.baseSearch.fields[0],
          hSearchInfo.keyField,
          token.replace('.', ','),
          {
            where: _condition,
            distinct: true,
            noLimit: true,
            outFields: [hSearchInfo.baseSearch.fields[0]],
            sort: this.sortAutocomplete,
          }
        );
      })
    );
  }

  private sortAutocomplete(searchItems: SearchListItem[]) {
    return searchItems.sort((a, b) => {
      if (typeof a.name === 'string' && typeof b.name === 'string') {
        return a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      } else if (typeof a.name === 'number' && typeof b.name === 'number') {
        return a.name - b.name;
      } else {
        return 0;
      }
    });
  }

  private formateKilometrierung(obj: object, fields: string[]) {
    let name = '';

    let i = 0;
    for (const f of fields) {
      if (i === 1 && fields[1]) {
        name += ' - ';
      }

      name += obj[f] + ' ';
      i++;
    }
    return name;
  }

  /**
   * @name searchParzelle
   * @description search parzellen based on gemeinde and parzellennr.
   */

  public searchHektometer() {
    this.selectionService.removeAllSelectedGraphics();
    this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.wait);
    const hSearchInfo: SearchInfo = this.getSearchConfig();
    const _layer = this._layerContainerService.layerList.filter(f => f.esriLayer.id === hSearchInfo.fkLayer)[0];
    // define query
    const nummerierung = this.nummerierungRef.nativeElement.value;
    let where = `${hSearchInfo.baseSearch.fields[1]} = ${
      nummerierung ? (nummerierung as string).match(/[^-]*/g)[0] : ''
    }`;

    const km = this.kmRef.nativeElement.value;
    where = where + ` AND ROUND(${hSearchInfo.baseSearch.fields[0]}, 2) = ROUND(${km}, 2)`;

    const query: __esri.Query = this._queryService.buildQuery(true, where);
    const errorCode = this.CONST.LAYER_ERROR.GET_FEATURES_FROM_LAYER_FAILED;
    // execute query
    this._queryService.execute(_layer.esriLayer['url'], query, errorCode).pipe(takeUntil(this.destroy$)).subscribe(
      result => {
        if (result.features.length === 0) {
          this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
          this._messageService.warning('NOTIFICATIONS.WARNING.SEARCH_HIERARCHY');
          return;
        }
        this.currentSelection = result.features[0];
        this.selectAndZoom(this.currentSelection);
        this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
      },
      error => {
        this._messageService.warning('NOTIFICATIONS.ERROR.SEARCH_HIERARCHICAL_INVALID');
        this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
      }
    );
  }

  public clearKMField() {
    (this.kmRef.nativeElement as HTMLInputElement).value = '';
  }

  private async selectAndZoom(feature: __esri.Graphic) {
    this.selectionService.addSelectedGraphic(this.currentSelection);
    await this.selectionService.goToFeature(feature, { animate: false }, 1000);
  }

  /**
   * @function getSearchConfig
   * @returns {string} search config infos
   * @description Get the hierachy search configuration for current workspace
   */
  private getSearchConfig(): HierarchicalSearchConfig {
    return this.searchConfig;
  }

  /* Dynamic Widget methods
 ========================== */

  /**
   * @function setInitValues
   * @param id
   * @param expand
   * @description Sets the initial values
   */
  public setInitValues(id: string, expand: boolean) {
    super.setInitValues(id, expand);
    this.searchConfig = this._configService.searchConfig.hierarchicalSearch.find(
      t => t.id === 'hektometer_search'
    ) as HierarchicalSearchConfig;
  }

  /**
   * @function destroySelf
   * @description First notify with componentDestroySubject that this component
   * will be destroyed. Destroy self and the widget reference from DOM and also from memory
   */
  public destroySelf() {
    if (this.currentSelection) this.selectionService.removeSelectedGraphic(this.currentSelection);
    super.destroySelf();
  }

  /**
   * @function destroySelf
   * @description Destroy the Esri Expand Widget of this component
   */
  public destroyWidget() {
    if (this.currentSelection) this.selectionService.removeSelectedGraphic(this.currentSelection);
    super.destroyWidget();
  }
}
